import { AlertTriangle, CheckCircle, Info, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { VolumeImbalance } from "@/lib/volume-balance";

interface ImbalanceAlertsProps {
  imbalances: VolumeImbalance[];
}

const severityConfig = {
  low: {
    icon: Info,
    color: "text-info",
    bg: "bg-info/10",
    badge: "bg-info/15 text-info",
  },
  medium: {
    icon: TrendingUp,
    color: "text-warning",
    bg: "bg-warning/10",
    badge: "bg-warning/15 text-warning",
  },
  high: {
    icon: AlertTriangle,
    color: "text-destructive",
    bg: "bg-destructive/10",
    badge: "bg-destructive/15 text-destructive",
  },
};

const typeLabels: Record<VolumeImbalance["type"], string> = {
  "push-pull": "Push/Pull",
  "upper-lower": "Upper/Lower",
  "bilateral": "Coverage",
  "agonist-antagonist": "Muscle Pair",
};

export function ImbalanceAlerts({ imbalances }: ImbalanceAlertsProps) {
  if (imbalances.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-success" />
            Balance Check
          </CardTitle>
          <CardDescription>Your training appears well-balanced</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 rounded-lg bg-success/10">
            <CheckCircle className="h-8 w-8 text-success" />
            <div>
              <p className="font-medium text-success">Great job!</p>
              <p className="text-sm text-muted-foreground">
                No significant imbalances detected in your training volume
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort by severity (high first)
  const sortedImbalances = [...imbalances].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.severity] - order[b.severity];
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          Imbalance Alerts
        </CardTitle>
        <CardDescription>
          Areas that may need attention in your training
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {sortedImbalances.map((imbalance, index) => {
          const config = severityConfig[imbalance.severity];
          const Icon = config.icon;

          return (
            <div
              key={index}
              className={`p-3 rounded-lg border ${config.bg}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 ${config.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="secondary" className={config.badge}>
                      {imbalance.severity}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {typeLabels[imbalance.type]}
                    </Badge>
                  </div>
                  <p className="font-medium text-sm">{imbalance.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    💡 {imbalance.recommendation}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
